import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { isProfileComplete } from '../../lib/rolePermissions';
import SEO from '../common/SEO';
import '../../styles/Auth.css';

/**
 * Protects routes that require authentication.
 * Shows a loading skeleton while checking session.
 * Redirects to /login if not authenticated, preserving the intended destination.
 * Enforces mandatory profile completion before allowing release submission or draft access.
 */
export default function ProtectedRoute({ children }) {
  const { isAuthenticated, profile, roles, loading, sessionChecked } = useAuth();
  const location = useLocation();

  // Still checking session ,  show skeleton to prevent flash
  if (loading || !sessionChecked) {
    return (
      <div className="auth-skeleton">
        <div className="auth-skeleton-card">
          <div className="auth-skeleton-line medium" />
          <div className="auth-skeleton-line short" />
          <div className="auth-skeleton-line tall" />
          <div className="auth-skeleton-line" />
          <div className="auth-skeleton-line tall" />
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Save the attempted URL for post-login redirect via both query param and route state
    const targetPath = (location.pathname || '') + (location.search || '');
    const redirectUrl = targetPath ? `/login?redirect=${encodeURIComponent(targetPath)}` : '/login';
    return <Navigate to={redirectUrl} state={{ from: location }} replace />;
  }

  const isOnboardingPath = location.pathname === '/onboarding' || location.pathname === '/edit-profile';
  const targetPath = (location.pathname || '') + (location.search || '');

  // 1. If user already completed onboarding, don't stay on /onboarding
  if (location.pathname === '/onboarding' && isProfileComplete(profile, roles)) {
    return <Navigate to="/" replace />;
  }

  // 2. Submission routes & draft access requirement check
  const isSubmissionOrDraftPath =
    location.pathname.startsWith('/distribution') ||
    location.pathname.startsWith('/submission') ||
    location.pathname.startsWith('/drafts');

  const requiresCompletion = isSubmissionOrDraftPath || (profile && profile.onboarding_completed === false);

  // If user has not completed required profile fields before release submission, route to edit-profile
  if (!isOnboardingPath && profile && requiresCompletion && !isProfileComplete(profile, roles)) {
    try {
      if (targetPath && targetPath !== '/' && targetPath !== '/dashboard') {
        sessionStorage.setItem('auth_redirect_destination', targetPath);
      }
    } catch {
      // Ignore sessionStorage exceptions
    }

    const redirectQuery = targetPath && targetPath !== '/' && targetPath !== '/dashboard'
      ? `&redirect=${encodeURIComponent(targetPath)}`
      : '';
    return <Navigate to={`/edit-profile?firstTime=true${redirectQuery}`} state={{ from: location }} replace />;
  }

  return (
    <>
      <SEO noindex={true} />
      {children}
    </>
  );
}
